import { useCallback, useEffect, useRef, useState } from "react";
import useWebSocket from "react-use-websocket";
import { CHALLENGE_DEFAULT_HISTORY, INFINITE_QUERY_KEY, MAX_PER_PAGE } from "../consts/request";
import { useAuthUser, useIsAuthenticated, useSignIn } from 'react-auth-kit'
import { ETERNAL_REFRESH_TOKEN } from "../consts/auth";
// Get fresh token via orchestrator (single-flight across tabs)
import { waitForFreshToken } from "./tokenRefreshOrchestrator";

export const answerQueryKey = (id) => ["answer", String(id)];
export const tagsAutocompleteQueryKey = () => ["tags", "autocomplete"];
export const challengesUri = (game_id) =>
  `${process.env.REACT_APP_API_CHALLENGES_URL}/${game_id}.json`;
export const nubsUri = (game_id) =>
  `${process.env.REACT_APP_API_NUBS_URL}?game_id=${game_id}`;
export const nubsQueryKey = (game_id, task_id) =>
  task_id
    ? ["games", parseInt(game_id), "nubs", parseInt(task_id)]
    : ["games", parseInt(game_id), "nubs"];
export const challengesQueryKey = (game_id, term, page, per_page) =>
  term || (page && page !== 1) || (per_page && per_page !== MAX_PER_PAGE)
    ? ["games", parseInt(game_id), "challenges", term, page, per_page]
    : ["games", parseInt(game_id), "challenges"];
export const challengeStreamUri = (challenge_id) =>
  `${process.env.REACT_APP_API_STREAM_TASK_URL}/${challenge_id}.json`;
export const dashboardUri = () => process.env.REACT_APP_API_GAMES_URL;
export const dashboardQueryKey = (filters, history, page, per_page, term) =>
  filters ||
  (history && history !== CHALLENGE_DEFAULT_HISTORY) ||
  (page && page !== 1) ||
  (per_page && per_page !== MAX_PER_PAGE) ||
  term
    ? ["games", filters, history, page, per_page, term]
    : ["games"];
export const gameUri = (game_id) =>
  `${process.env.REACT_APP_API_GAME_RETRIEVE_URL}/${game_id}.json`;
export const gameBrandingUri = (game_id) =>
  `${process.env.REACT_APP_API_BRANDING_URL}/${game_id}/grey.json`;
export const gameStreamUri = (game_id) =>
  `${process.env.REACT_APP_API_STREAM_GAME_URL}/${game_id}.json`;
export const leaderboardUri = (game_id) =>
  `${process.env.REACT_APP_API_LEADERBOARD_URL}?game_id=${game_id}`;
export const leaderboardQueryKey = (game_id, style, query, metric) =>
  style || query || metric
    ? ["leaderboard", game_id, style, query, metric]
    : ["leaderboard", game_id];
export const libraryUri = (game_id) =>
  `${process.env.REACT_APP_API_LIBRARY_URL}?game_id=${game_id}`;
export const libraryQueryKey = (game_id, page, per_page, terms = []) =>
  (page && page !== 1) || (per_page && per_page !== MAX_PER_PAGE) || terms.length
    ? ["library", game_id, page, per_page].concat(terms)
    : ["library", game_id];

export const globalMonitorQueryKey = (game_id) => [
  "stream",
  "monitor",
  game_id,
];
export const monitorQueryKey = (game_id, task_id = null, team_id = null, page, per_page = MAX_PER_PAGE, type = null) =>
  task_id || team_id || type
    ? ["stream", "monitor", parseInt(game_id), task_id, team_id, page, per_page, type]
    : ![null, undefined].includes(page)
      ? ["stream", "monitor", parseInt(game_id), page, per_page || MAX_PER_PAGE]
      : ["stream", "monitor", parseInt(game_id)];
export const monitorStreamQueryKey = (
  game_id,
  task_id,
  team_id,
  page,
  per_page
) =>
  task_id || team_id
    ? ["stream", "monitor", parseInt(game_id), task_id, team_id, page, per_page]
    : ["stream", "monitor", parseInt(game_id), page || 1, per_page || MAX_PER_PAGE];

export const playersQueryKey = (game_id) => teamsQueryKey(game_id, 1, INFINITE_QUERY_KEY);
export const personalizationsUri = (game_id) =>
  `${process.env.REACT_APP_API_PERSONALIZATIONS_URL}.json?game_id=${game_id}`;
export const rewardsUri = (game_id) =>
  `${process.env.REACT_APP_API_REWARD_RETRIEVE_URL}?game_id=${game_id}`;
export const rewardsQueryKey = (game_id, page, per_page) =>
  (page && page !== 1) || (per_page && per_page !== MAX_PER_PAGE)
    ? ["rewards", game_id, page, per_page]
    : ["rewards", game_id];
export const teamsUri = (game_id) =>
  `${process.env.REACT_APP_API_TEAM_RETRIEVE_URL}?game_id=${game_id}`;
export const teamsQueryKey = (game_id, page, per_page, term) =>
  term || (page && page !== 1) || (per_page && per_page !== MAX_PER_PAGE)
    ? ["teams", parseInt(game_id), page, per_page, term]
    : ["teams", parseInt(game_id)];
export const teamDetailQueryKey = (id) => ["team-detail", id];
export const teamStreamUri = (team_id) =>
  `${process.env.REACT_APP_API_STREAM_TEAM_URL}/${team_id}.json`;

export const challengeStreamQueryKey = (game_id, challenge_id) => [
  "stream",
  game_id?.toString(),
  "challenge",
  challenge_id?.toString(),
];
export const gameStreamQueryKey = (game_id) => ["stream", game_id?.toString()];
export const teamStreamQueryKey = (game_id, team_id) => [
  "stream",
  game_id?.toString(),
  "team",
  team_id?.toString(),
];
export const groupsQueryKey = (game_id, page, per_page) =>
  (page && page !== 1) || (per_page && per_page !== MAX_PER_PAGE)
    ? ["groups", parseInt(game_id), page, per_page]
    : ["groups", parseInt(game_id)];
export const analyticsQueryKey = (game_id) => ["analytics", parseInt(game_id)];
export const gameEventUri = () => `${process.env.REACT_APP_API_GAME_EVENT_URL}`;
export const infiniteAnswerCommentsUri = (answer_id) => `${process.env.REACT_APP_API_ANSWERS_COMMENTS_URL}/${answer_id}.json`;
export const npcsUri = (game_id) => `${process.env.REACT_APP_API_NPCS_URL}?game_id=${game_id}`;
export const npcsQueryKey = (game_id, term, page, per_page) =>
  term || (page && page !== 1) || (per_page && per_page !== MAX_PER_PAGE)
    ? ["games", parseInt(game_id), "npcs", term, page, per_page]
    : ["games", parseInt(game_id), "npcs"];
export const designationsUri = (game_id) => `${process.env.REACT_APP_API_DESIGNATION_URL}?game_id=${game_id}`;
export const designationsQueryKey = (game_id, term, page, per_page) =>
  term || (page && page !== 1) || (per_page && per_page !== MAX_PER_PAGE)
    ? ["games", parseInt(game_id), "designations", term, page, per_page]
    : ["games", parseInt(game_id), "designations"];

// answer object is optionally passed from the websocket event handler
export const infiniteAnswerCommentsQueryKey = (answer_id, { detail } = {}) => ["infinite_answer_comment", (detail?.answer?.id || answer_id)?.toString(), INFINITE_QUERY_KEY];
export const answerCommentsQueryKey = ({ answerId, page }) => ["answer_comments", answerId?.toString(), page];
export const answerAdjustmentsQueryKey = ({ answerId }) => ["answer_adjustments", answerId?.toString()];
export const judgeQueryKey = (gameId, taskId) => 
    taskId ? ['judges', gameId, taskId] : ['judges', gameId];
export const zonesQueryKey = (gameId) => ["zones", gameId];
export const waypointsQueryKey = (gameId, zoneId) => ["waypoints", gameId, zoneId];

const whitelabel = process.env.REACT_APP_WHITELABEL;
const useRefresh = ({ subscriptions }) => {
  const isAuthenticated = useIsAuthenticated();
  const auth = useAuthUser()();
  const refreshingRef = useRef(false)
  const signIn = useSignIn()
  const { token } = auth || {};
  const isLoggedIn = isAuthenticated();
  const [connectionOpened, setConnectionOpened] = useState(true)

  const { lastJsonMessage, getWebSocket } = useWebSocket("wss://ws.socialscavenger.com", {
    onError: () => {
      handleTokenRefresh()
    },
    retryOnError: isLoggedIn,
    shouldReconnect: ({ wasClean }) => isLoggedIn && !wasClean,
    queryParams: {
      token: encodeURIComponent(token),
      whitelabel,
      subscriptions,
    },
  }, connectionOpened);

  useEffect(() => {
    // Proactively close the websocket every 10 mins and re-open. 
    // This is because Amazon times out websockets after 15 minutes but doesn't always do so cleanly.
    const pollingDelay = 10 * 60 * 1000
    const pollingTimeout = setTimeout(() => {
        setConnectionOpened(false)
    }, pollingDelay)

    return () => {
        clearTimeout(pollingTimeout)
    }
  }, [])

  // Centralized reconnect logic
  const reconnectWebSocket = useCallback((reason = "Manual reconnect") => {
    const socket = getWebSocket();
    if (socket && socket.readyState === WebSocket.OPEN) {
      console.debug("REFTOKEN: WS hook closing connection");
      socket.close(1000, reason);
    }
    console.debug(`REFTOKEN: WS hook refreshing connection for ${reason}`);
    setConnectionOpened(false);
  }, [getWebSocket]);

  // Token refresh logic, safe against multiple concurrent calls
  const handleTokenRefresh = async (reconnectReason) => {
    if (isLoggedIn || !localStorage.getItem(ETERNAL_REFRESH_TOKEN)) return;
    if (refreshingRef.current) return;

    refreshingRef.current = true;
    try {
      console.debug("REFTOKEN: WS hook attempting token refresh...");

      const authPayload = await waitForFreshToken({
        currentAuthState: auth,
      });

      if (authPayload) {
        signIn(authPayload);
        reconnectWebSocket(reconnectReason);
      }
    } catch (err) {
      console.debug(`REFTOKEN: WS hook token refresh failed: ${err}`);
    } finally {
      refreshingRef.current = false;
    }
  };

  useEffect(() => {
    if (!connectionOpened) {
        setConnectionOpened(true)
    }
  }, [connectionOpened])

  return lastJsonMessage || { id: null };
};

export default useRefresh;
